import "./IssuanceSummary.css";

import { ProjectIssuanceConstants } from "../../../../constants";
import { ProjectIssuanceSpecificProperty, ProjectIssuanceSpecificSchema } from "../../../../models";
import { convertDateToStringDDMMYYYY, formatNumber } from "../../../../utils";
import { Column, DynamicFormFieldsFromJsonSchema, ReadonlyText, Row } from "../../../../widget";
import {
  getConditionalEnumValues,
  normaliseFieldLabel,
  shouldRenderSchemaField,
} from "../../../IssuancePanel/components/IssuancePanelDynamic/utils";
import { isUnitKmHabitatType, parseStandardSpecificIssuanceData } from "../../../IssuancePanel/utils";

export interface IssuanceSummaryProps {
  issuanceSchema?: ProjectIssuanceSpecificSchema;
  standardSpecificIssuanceData?: unknown;
  projectName: string;
  /*
    Absent when the summary describes a project's aggregated inventory rather than one issuance (the org-wide
    inventory board), where there is no single issuance status - and so no issuance date - to report.
  */
  status?: string;
  issuanceDate?: Date | null;
  areaHaRemaining?: number | null;
  unitQuantityTotal: number;
  unitQuantityAvailable: number;
  unitQuantityReserved: number;
  unitQuantitySold: number;
  quotedUnits: number;
  variant?: "hub" | "seed";
}

interface SummaryField {
  key: string;
  element: JSX.Element;
  isFullWidth?: boolean;
}

/*
  The size fields carry the issuance's habitat area, so the summary labels them the way the design does rather
  than sentence-casing the schema key ("Unit ha").
*/
const SUMMARY_FIELD_LABEL_OVERRIDES: Record<string, string> = {
  unitHa: "Total size (Ha)",
  unitKm: "Total size (Km)",
};

/*
  Built as a flat list so each field lands in the next free half-row whichever ones the schema leaves out -
  otherwise hiding a single field would strand its partner and leave a gap mid-row. Full-width fields (table
  repeaters, paragraphs) take a row of their own.
*/
const renderSummaryRows = (summaryFields: SummaryField[]): JSX.Element[] => {
  const rows = summaryFields.reduce<SummaryField[][]>((accumulatedRows, field) => {
    const currentRow = accumulatedRows[accumulatedRows.length - 1];

    if (field.isFullWidth || !currentRow || currentRow.length === 2 || currentRow[0].isFullWidth) {
      accumulatedRows.push([field]);
    } else {
      currentRow.push(field);
    }

    return accumulatedRows;
  }, []);

  return rows.map((rowFields) => (
    <Row spacingV="ml" key={rowFields.map((field) => field.key).join("_")}>
      {rowFields.map((field) => (
        <Column span={field.isFullWidth ? 12 : 6} key={field.key}>
          {field.element}
        </Column>
      ))}
    </Row>
  ));
};

export const IssuanceSummary = ({
  issuanceSchema,
  standardSpecificIssuanceData,
  projectName,
  status,
  issuanceDate,
  areaHaRemaining,
  unitQuantityTotal,
  unitQuantityAvailable,
  unitQuantityReserved,
  unitQuantitySold,
  quotedUnits,
  variant = "seed",
}: IssuanceSummaryProps): JSX.Element => {
  const showIssuanceDate = status !== undefined && status !== ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT;

  /*
    The standard-specific fields are whatever the project's issuance schema declares - BNG's habitat and size,
    carbon's vintage dates, and anything a future standard adds - so the summary renders them from the schema
    with the same tests, labels and enum resolution IssuancePanelDynamic uses to build its inputs. That keeps
    the summary in step with the edit panel instead of hard-coding one standard's fields.
  */
  const schemaProperties = issuanceSchema?.properties ?? {};
  const issuanceData = parseStandardSpecificIssuanceData(standardSpecificIssuanceData);

  const getFieldValue = (fieldKey: string): unknown => issuanceData[fieldKey];

  // Linear habitats store their size in unitKm and null out unitHa - see IssuancePanelDynamic.
  const habitatTypeValue = getFieldValue("habitatType");
  const isKm = isUnitKmHabitatType(typeof habitatTypeValue === "string" ? habitatTypeValue : null);

  const shouldRenderSummaryField = (fieldKey: string, fieldDefinition: ProjectIssuanceSpecificProperty): boolean => {
    if (!shouldRenderSchemaField(fieldKey, fieldDefinition)) {
      return false;
    }

    if (fieldKey !== "unitKm" && fieldKey !== "unitHa") {
      return true;
    }

    return fieldKey === "unitKm" ? isKm : !isKm;
  };

  const formatReadonlyValue = ({
    fieldDefinition,
    fieldValue,
  }: {
    fieldDefinition: ProjectIssuanceSpecificProperty;
    fieldValue: unknown;
  }): string | undefined => {
    if (fieldValue instanceof Date) {
      return convertDateToStringDDMMYYYY(fieldValue);
    }

    if (typeof fieldValue === "boolean") {
      return fieldValue ? "Yes" : "No";
    }

    if (typeof fieldValue === "number" && !fieldDefinition.enum) {
      return formatNumber(fieldValue, fieldDefinition.decimalPlaces);
    }

    return undefined;
  };

  const fixedLeadingFields: SummaryField[] = [
    { key: "projectName", element: <ReadonlyText label="Project name" value={projectName} /> },
    ...(showIssuanceDate
      ? [
          {
            key: "issuanceDate",
            element: (
              <ReadonlyText
                label="Issuance date"
                value={issuanceDate ? convertDateToStringDDMMYYYY(issuanceDate) : undefined}
                noDataAvailable={!issuanceDate}
              />
            ),
          },
        ]
      : []),
  ];

  /*
    Remaining size is derived rather than declared on the schema, so it is threaded in right after the size
    field it belongs with.
  */
  const remainingSizeField: SummaryField = {
    key: "remainingSize",
    element: (
      <ReadonlyText
        label="Remaining size"
        value={formatNumber(areaHaRemaining ?? undefined)}
        noDataAvailable={areaHaRemaining === null || areaHaRemaining === undefined}
      />
    ),
  };

  const fixedTrailingFields: SummaryField[] = [
    { key: "unitQuantityTotal", element: <ReadonlyText label="Total units" value={formatNumber(unitQuantityTotal)} /> },
    {
      key: "unitQuantityAvailable",
      element: <ReadonlyText label="Available units" value={formatNumber(unitQuantityAvailable)} />,
    },
    {
      key: "unitQuantityReserved",
      element: <ReadonlyText label="Reserved units" value={formatNumber(unitQuantityReserved)} />,
    },
    { key: "unitQuantitySold", element: <ReadonlyText label="Sold units" value={formatNumber(unitQuantitySold)} /> },
    { key: "quotedUnits", element: <ReadonlyText label="Quoted units" value={formatNumber(quotedUnits)} /> },
  ];

  return (
    <div className={`IssuanceSummary IssuanceSummary_${variant}`}>
      <Row spacingV="ml">
        <Column span={12}>
          <p className="body01 IssuanceSummary_header">Issuance summary</p>
        </Column>
      </Row>
      <DynamicFormFieldsFromJsonSchema<ProjectIssuanceSpecificProperty>
        schemaProperties={schemaProperties}
        getFieldValue={getFieldValue}
        isReadonly
        shouldRenderField={shouldRenderSummaryField}
        getFieldLabel={(fieldKey) => SUMMARY_FIELD_LABEL_OVERRIDES[fieldKey] ?? normaliseFieldLabel(fieldKey)}
        getEnumValues={(fieldKey, fieldDefinition, getSchemaFieldValue) =>
          fieldDefinition.enum ?? getConditionalEnumValues(issuanceSchema, fieldKey, getSchemaFieldValue)
        }
        formatReadonlyValue={formatReadonlyValue}
        renderLayout={(schemaFields) => {
          const schemaSummaryFields = schemaFields.flatMap<SummaryField>((field) =>
            field.key === "unitHa" || field.key === "unitKm"
              ? [{ key: field.key, element: field.element, isFullWidth: field.isFullWidth }, remainingSizeField]
              : [{ key: field.key, element: field.element, isFullWidth: field.isFullWidth }]
          );

          return <>{renderSummaryRows([...fixedLeadingFields, ...schemaSummaryFields, ...fixedTrailingFields])}</>;
        }}
      />
    </div>
  );
};
